/**
 * Product-card arrow colour vs. the photo behind it.
 *
 * Simple rule: dark photo -> white arrows, light photo -> black arrows. We
 * sample the current slide's overall brightness on a tiny canvas and set
 * data-arrow-tone="light" | "dark" on the <slideshow-arrows> element; CSS does
 * the rest. Both arrows always share the one tone (whole-image decision).
 *
 * Performance: lazy via IntersectionObserver, one shared canvas, results
 * memoised per image src, re-measured only on slide change. Cross-origin images
 * that taint the canvas fall back to the CSS default silently.
 */
(() => {
  const ARROWS_SELECTOR =
    '.card-gallery slideshow-arrows:has(.slideshow-control--style-directional-chevron)';

  // Mean perceived brightness (0..255) at/above which the photo counts as light.
  const LIGHT_CUTOFF = 150;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const taintedSrc = new Set();
  const toneCache = new Map(); // src -> 'light' | 'dark'

  /** 'light' | 'dark' for an image's overall brightness, or null if unmeasurable. */
  function toneFor(img) {
    const src = img.currentSrc || img.src;
    if (!src || taintedSrc.has(src)) return null;
    const cached = toneCache.get(src);
    if (cached) return cached;

    if (!img.complete || !img.naturalWidth) return null;

    // Downscale the whole image into a tiny box — cheap, and averaging many
    // pixels gives the overall light/dark feel rather than one local spot.
    const w = 16;
    const h = 16;
    canvas.width = w;
    canvas.height = h;
    let data;
    try {
      ctx.drawImage(img, 0, 0, w, h);
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (_err) {
      taintedSrc.add(src);
      return null;
    }
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    }
    const mean = sum / (data.length / 4);
    const tone = mean >= LIGHT_CUTOFF ? 'dark' : 'light';
    toneCache.set(src, tone);
    return tone;
  }

  /** The image of the slide currently shown (falls back to the first image). */
  function activeImage(arrows, gallery) {
    const target = arrows._targetSlide;
    if (target && target.isConnected) {
      const img = target.querySelector('img');
      if (img) return img;
    }
    return (
      gallery.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      gallery.querySelector('.slideshow-slide:not([hidden]) img') ||
      gallery.querySelector('img')
    );
  }

  function apply(arrows) {
    const gallery = arrows.closest('.card-gallery');
    if (!gallery) return;
    const img = activeImage(arrows, gallery);
    if (!img) return;

    if (!img.complete || !img.naturalWidth) {
      const pending = img;
      const onReady = () => {
        if (activeImage(arrows, gallery) === pending) schedule(arrows);
      };
      if (typeof pending.decode === 'function') pending.decode().then(onReady, onReady);
      else pending.addEventListener('load', onReady, { once: true });
      return;
    }

    const tone = toneFor(img);
    if (tone) arrows.setAttribute('data-arrow-tone', tone);
    else arrows.removeAttribute('data-arrow-tone');
  }

  /* ----- batched rAF scheduler ----- */
  const queue = new Set();
  let rafId = 0;
  function flush() {
    rafId = 0;
    for (const arrows of queue) {
      queue.delete(arrows);
      if (arrows.isConnected) apply(arrows);
    }
  }
  function schedule(arrows) {
    queue.add(arrows);
    if (!rafId) rafId = requestAnimationFrame(flush);
  }

  // Coalesce slide-change bursts and let the DOM settle before measuring.
  const deferPending = new WeakSet();
  function scheduleDeferred(arrows) {
    if (deferPending.has(arrows)) return;
    deferPending.add(arrows);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        deferPending.delete(arrows);
        schedule(arrows);
      })
    );
  }

  /* ----- wiring ----- */
  const wired = new WeakSet();
  function wire(arrows) {
    if (wired.has(arrows)) return;
    wired.add(arrows);
    const gallery = arrows.closest('.card-gallery');
    if (gallery) {
      gallery.addEventListener(
        'slideshow:select',
        (event) => {
          const slide = event.detail?.slide;
          if (slide instanceof Element) arrows._targetSlide = slide;
          scheduleDeferred(arrows);
        },
        { passive: true }
      );
    }
    schedule(arrows);
  }

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                obs.unobserve(entry.target);
                wire(entry.target);
              }
            }
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  function register(arrows) {
    if (io) io.observe(arrows);
    else wire(arrows);
  }

  function scan(root) {
    if (root.matches?.(ARROWS_SELECTOR)) register(root);
    root.querySelectorAll?.(ARROWS_SELECTOR).forEach(register);
  }

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1) scan(node);
      }
    }
  });

  function init() {
    scan(document);
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
